import type { MouseEvent } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Import, Upload } from '@mint-lib/icons';

import { Button } from '../../lib/button.jsx';
import { Divider } from '../../lib/divider.jsx';
import { typographyVariants } from '../../lib/typography.jsx';
import { ChildrenFnArgs } from './types.js';

export const FileUploaderContent = ({
  getRootProps,
  getInputProps,
  isDragActive,
  open,
  disabled,
  variant = 'default',
  buttonLabel,
}: ChildrenFnArgs) => {
  const componentVariantsMap = {
    default: DropAreaWithButton,
    area: DropArea,
    button: FileUploaderButton,
  };
  const VariantComponent = componentVariantsMap[variant];

  return (
    <VariantComponent
      {...{
        getRootProps,
        getInputProps,
        isDragActive,
        open,
        disabled,
        variant,
        buttonLabel,
      }}
    />
  );
};

const DropAreaWithButton = ({
  getRootProps,
  getInputProps,
  isDragActive,
  open,
  disabled,
}: ChildrenFnArgs) => {
  const { t } = useTranslation('@mint-lib/tw-components');

  return (
    <div
      data-state={isDragActive ? 'active' : 'inactive'}
      className="w-full min-w-116.5 rounded-sm border border-dashed border-border-secondary bg-ui-primary text-text-secondary hover:border-border-disabled hover:bg-background-tertiary aria-[disabled]:bg-ui-primary data-[state=active]:border-tertiary-main data-[state=active]:bg-background-tertiary"
      aria-disabled={disabled}
      {...getRootProps({
        isDragActive,
        disabled,
        onClick: (event: MouseEvent) => {
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
        },
      })}
    >
      <input {...getInputProps()} />
      <div className="flex h-20 flex-row items-center justify-center gap-3 p-2">
        <p
          className={typographyVariants({
            variant: 'bodyShort02',
            color: disabled ? 'disabled' : 'secondary',
          })}
        >
          {t('Drag & drop here to start uploading')}
        </p>
        <div className="grid h-20 grid-rows-[1fr_min-content_1fr] items-center justify-center gap-2 py-2">
          <Divider orientation="vertical" className="ml-2.5" />
          <p
            className={typographyVariants({
              variant: 'bodyShort01',
              color: disabled ? 'disabled' : 'secondary',
            })}
          >
            OR
          </p>
          <Divider orientation="vertical" className="ml-2.5" />
        </div>
        <Button
          onClick={open}
          disabled={disabled}
          variant="outlined"
          color="primary"
          type="button"
        >
          <Upload /> {t('Browse files')}
        </Button>
      </div>
    </div>
  );
};

const DropArea = ({
  getRootProps,
  getInputProps,
  isDragActive,
  disabled,
}: ChildrenFnArgs) => {
  return (
    <div
      aria-disabled={disabled}
      data-state={isDragActive}
      className="flex h-20 cursor-pointer items-center justify-center bg-ui-primary outline-none aria-[disabled]:text-text-disabled"
      {...getRootProps({
        isDragActive,
        disabled,
      })}
    >
      <input {...getInputProps()} />

      <div className="flex h-8 w-8 items-center justify-center rounded-[3px] border-[0.5px] border-dashed border-violet-main bg-background-violet">
        <Import className="fill-inherit" />
      </div>
    </div>
  );
};

const FileUploaderButton = ({
  getInputProps,
  open,
  disabled,
  buttonLabel,
}: ChildrenFnArgs) => {
  const { t } = useTranslation('@mint-lib/tw-components');

  return (
    <>
      <input {...getInputProps()} />
      <Button
        onClick={open}
        disabled={disabled}
        color="primary"
        variant="outlined"
        size="large"
      >
        <Upload />
        {buttonLabel ?? t('Upload files')}
      </Button>
    </>
  );
};
